import { ChangeEvent } from 'react';
import BackArrow from '../../assets/backArrowIcon.svg';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import Input from '../../component/Input/input';
import Button from '../../component/button/button';
import FileUpload from '../../component/fileUpload/fileUpload';
import './onboarding.css';
import { RenderUnifiedViewProps } from './onboarding.type';
import SignatureCanvas from 'react-signature-canvas';
import { UserType } from '@inplay/models';
import { Trans } from 'react-i18next';
import CameraIcon from '../../assets/cameraIcon.svg';
import Camera from '../../component/camera/Camera';
import PhotoReview from '../../component/photoReview/PhotoReview';
import DesktopView from '../../component/onboardingViews/DesktopView';
import TabletView from '../../component/onboardingViews/TabletView';
import Download from '../../assets/download_signature.png';
import Delete from '../../assets/delete.png';

// Function to get organization unit field label based on user type
export const getOrganizationUnitLabel = (
  userType: UserType,
  t?: any,
): string => {
  if (!t) return '';
  switch (userType) {
    case UserType.ORGANIZATION_ADMIN:
      return t('onboarding.organization');
    case UserType.SUB_ORGANIZATION_ADMIN:
      return t('onboarding.subOrganization');
    case UserType.VENUE_ADMIN:
      return t('onboarding.venue');
    case UserType.VENUE_MANAGER:
    case UserType.VENUE_STAFF:
      return t('onboarding.venueBranches');
    default:
      return t('onboarding.organizationUnit');
  }
};

const videoConstraints = {
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  facingMode: 'user',
};

// Unified render function for all three steps
export const renderUnifiedView = ({
  organizationUnit,
  currentStep,
  profileData,
  handleChange,
  handleFileSelect,
  handleFileDeselect,
  handleContinue,
  handleSkip,
  handleBack,
  isLoading,
  errorMessages,
  isDesktop,
  userType,
  t,
  showCamera,
  photoReview,
  handleTakePhoto,
  handlePhotoCapture,
  handleRetake,
  handleCloseButton,
  handleSignatureDrawingClick,
  handleUpload,
  isDrawing,
  sigCanvas,
  handleDelete,
  isSigned,
  handleSignBegin,
  imagePreview,
  fileInputRef,
}: RenderUnifiedViewProps) => {
  // Render step content based on current step
  const renderStepContent = () => {
    if (currentStep === 1) {
      // Step 1: Basic Information
      return (
        <>
          <div className="mb-8">
            <p className="text-[16px]">1/4</p>
            <h1 className="text-[32px] leading-[72px] font-medium text-[#1A1A1A]">
              <Trans i18nKey="onboarding.step1Title" />
              {process.env.REACT_APP_WEB_TITLE}
            </h1>
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Input
                label={t('onboarding.firstName')}
                value={profileData.firstName}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleChange('firstName', event.target.value)
                }
                placeholder={t('onboarding.firstNamePlaceholder')}
                errorMessage={errorMessages.firstName}
                inputClassName={`onboarding-input ${
                  errorMessages.firstName
                    ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                    : ''
                }`}
              />
            </div>

            <div className="space-y-2">
              <Input
                label={t('onboarding.lastName')}
                value={profileData.lastName}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleChange('lastName', event.target.value)
                }
                placeholder={t('onboarding.lastNamePlaceholder')}
                errorMessage={errorMessages.lastName}
                inputClassName={`onboarding-input ${
                  errorMessages.lastName
                    ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                    : ''
                }`}
              />
            </div>

            <div className="space-y-2">
              <Input
                label={t('onboarding.userType')}
                disabled={true}
                value={userType}
                placeholder={t('onboarding.userType')}
                errorMessage={errorMessages.userType}
                inputClassName={`onboarding-input ${
                  errorMessages.userType
                    ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                    : ''
                }`}
              />
            </div>

            <div className="space-y-2">
              <Input
                label={getOrganizationUnitLabel(userType as UserType, t)}
                disabled={true}
                value={organizationUnit}
                placeholder={getOrganizationUnitLabel(userType as UserType, t)}
                errorMessage={errorMessages.organizationUnit}
                inputClassName={`onboarding-input ${
                  errorMessages.organizationUnit
                    ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                    : ''
                }`}
              />
            </div>

            <div className="pt-6">
              <Button
                onClick={handleContinue}
                className="w-full py-4 rounded-full font-helvetica bg-THEME_COLOR text-white hover:bg-THEME_HOVER_COLOR"
                disabled={isLoading}
              >
                {isLoading ? (
                  <ButtonSpinner />
                ) : (
                  <Trans i18nKey="onboarding.continue" />
                )}
              </Button>
            </div>
          </div>
        </>
      );
    } else if (currentStep === 2) {
      // Step 2: Profile Picture
      return (
        <>
          <div className="mb-28">
            <div
              onClick={handleBack}
              className="text-base flex gap-4 items-center cursor-pointer justify-start w-full"
            >
              <img src={BackArrow} alt="Back Arrow" className="w-5 h-4" />
              <p className="text-[16px]">2/4</p>
            </div>
            <h1 className="text-[32px] leading-[72px] font-medium text-[#1A1A1A]">
              <Trans i18nKey="onboarding.step2Title" />
            </h1>
          </div>

          <div className="flex flex-col items-center mb-28">
            <FileUpload
              previewImage
              onFileSelect={handleFileSelect}
              onDeselect={handleFileDeselect}
              previewUrl={profileData.profilePictureUrl}
              selectedFileName={profileData.fileName}
              buttonLabel={t('onboarding.upload')}
              label={t('onboarding.profilePicture')}
              supportedFormatsText={t('onboarding.supportedFormatsText')}
              acceptedTypes="image/jpeg, image/png"
              maxSize={2}
            />
          </div>

          <div className="space-y-8">
            <Button
              onClick={handleContinue}
              className="w-full py-4 rounded-full font-helvetica bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
              disabled={isLoading || !profileData.profilePicture}
            >
              {isLoading ? (
                <ButtonSpinner />
              ) : (
                <Trans i18nKey="onboarding.continue" />
              )}
            </Button>

            <div
              className="text-center text-sm cursor-pointer hover:underline"
              onClick={handleSkip}
            >
              <Trans i18nKey="onboarding.skip" />
            </div>
          </div>
        </>
      );
    } else if (currentStep === 3) {
      //step 3  signature
      return (
        <>
          <div className="mb-28">
            <div
              onClick={handleBack}
              className="text-base flex gap-4 items-center cursor-pointer justify-start w-full"
            >
              <img src={BackArrow} alt="Back Arrow" className="w-5 h-4" />
              <p className="text-[16px]">3/4</p>
            </div>
            <h1 className="text-[32px] leading-[72px] font-medium text-[#1A1A1A]">
              <Trans i18nKey="onboarding.step3Title" />
            </h1>
          </div>
          <div className="flex flex-col items-center mb-28">
            {!isDrawing ? (
              <div
                className="relative border border-dashed border-[#A3A3A3] rounded-md text-center max-w-full w-full mx-auto cursor-pointer overflow-hidden"
                style={{ height: 175 }}
              >
                {imagePreview ? (
                  <div className="relative flex justify-center items-center">
                    <img
                      src={Delete}
                      alt="Delete"
                      onClick={handleDelete}
                      className="w-5 h-5 absolute top-2 right-2 cursor-pointer z-10"
                    />

                    <img
                      src={imagePreview}
                      alt="Uploaded preview"
                      className="max-w-xs max-h-[175px] rounded m-5"
                    />
                  </div>
                ) : (
                  <div
                    className="relative border-dashed rounded-md text-center max-w-full w-full mx-auto cursor-pointer overflow-hidden"
                    style={{ height: 175 }}
                  >
                    {imagePreview !== null ? (
                      <div className="relative flex justify-center items-center h-full">
                        <img
                          src={Delete}
                          alt="Delete"
                          onClick={handleDelete}
                          className="w-5 h-5 absolute top-2 right-2 cursor-pointer z-10"
                        />
                        <img
                          src={imagePreview}
                          alt="Uploaded preview"
                          className="max-w-xs max-h-[175px] rounded border"
                        />
                      </div>
                    ) : (
                      <button
                        className="flex flex-col h-full w-full"
                        onClick={handleSignatureDrawingClick}
                      >
                        <p className="text-[#A3A3A3] text-[16px] mt-6">
                          <Trans i18nKey="onboarding.drawByHand" />{' '}
                          <Trans i18nKey="onboarding.uploadSignature" />
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleUpload}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="hidden"
                          ref={fileInputRef}
                        />
                        <div className="flex flex-col items-center mt-5">
                          <img
                            src={Download}
                            alt="Upload Icon"
                            className="w-8 h-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                          />
                          <div
                            className="w-32 py-1.5 rounded-full bg-white border text-[#1A1A1A] hover:text-[#1A1A1A] text-sm border-[#D8D8D8] hover:bg-white mt-5 text-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                          >
                            <Trans i18nKey={'onboarding.browseFile'} />
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="relative max-w-full w-full mx-auto">
                {isSigned && (
                  <div onClick={handleDelete} className="cursor-pointer">
                    <img
                      src={Delete}
                      alt="Signature"
                      className="w-5 h-5 absolute top-8 right-8 text-sm text-red-500 font-medium"
                    />
                  </div>
                )}
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  onBegin={handleSignBegin}
                  canvasProps={{
                    height: 175,
                    className:
                      'border-2 rounded-md border-dashed w-full border-[#707070] rounded-[10px]',
                  }}
                />
              </div>
            )}
          </div>

          <div className="space-y-8">
            <Button
              onClick={handleContinue}
              className="w-full py-4 rounded-full font-helvetica bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
              disabled={isLoading || (!imagePreview && !isSigned)}
            >
              {isLoading ? (
                <ButtonSpinner />
              ) : (
                <Trans i18nKey="onboarding.continue" />
              )}
            </Button>

            <div
              className="text-center text-sm cursor-pointer hover:underline"
              onClick={handleSkip}
            >
              <Trans i18nKey="onboarding.skip" />
            </div>
          </div>
        </>
      );
    } else if (currentStep === 4) {
      // Step 4: Review Details
      return (
        <>
          <div className="mb-8">
            <div
              onClick={handleBack}
              className="text-base flex gap-4 items-center  cursor-pointer justify-start w-full"
            >
              <img src={BackArrow} alt="Back Arrow" className="w-5 h-4" />
              <p className="text-[16px]">4/4</p>
            </div>
            <h1 className="text-[32px] leading-[72px] font-medium text-[#1A1A1A]">
              <Trans i18nKey="onboarding.step4Title" />
            </h1>
          </div>

          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-base text-black mb-1 font-helvetica">
                <Trans i18nKey="onboarding.firstName" />
              </label>
              <p className="text-gray-700">{profileData.firstName}</p>
            </div>

            <div>
              <label className="block text-base text-black mb-1 font-helvetica">
                <Trans i18nKey="onboarding.lastName" />
              </label>
              <p className="text-gray-700">{profileData.lastName}</p>
            </div>

            <div>
              <label className="block text-base text-black mb-1 font-helvetica">
                <Trans i18nKey="onboarding.userType" />
              </label>
              <p className="text-gray-700">{userType}</p>
            </div>

            <div>
              <label className="block text-base text-black mb-1 font-helvetica">
                {getOrganizationUnitLabel(userType as UserType, t)}
              </label>
              <p className="text-gray-700">{organizationUnit}</p>
            </div>

            {profileData.fileName && (
              <div>
                <label className="block text-base text-black mb-1 font-helvetica">
                  <Trans i18nKey="onboarding.profilePicture" />
                </label>
                <p className="text-gray-700">{profileData.fileName}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-base text-black mb-1 font-helvetica">
              <Trans i18nKey="onboarding.signature" />
            </label>
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Uploaded preview"
                className="max-w-xs max-h-[60px] rounded"
              />
            ) : (
              <p className="text-sm text-gray-700">
                <Trans i18nKey={'onboarding.noSignature'} />
              </p>
            )}
          </div>

          <div className="pt-6">
            <Button
              onClick={handleContinue}
              className="w-full py-4 rounded-full font-helvetica text-base bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <ButtonSpinner />
              ) : (
                <Trans i18nKey="onboarding.finish" />
              )}
            </Button>
          </div>
        </>
      );
    }
  };

  // Shared layout structure
  if (isDesktop) {
    return <DesktopView t={t}>{renderStepContent()}</DesktopView>;
  } else {
    if (currentStep === 2 && (showCamera || photoReview)) {
      return (
        <>
          {showCamera ? (
            <div className="fixed inset-0 z-50 bg-[#120000]">
              <Camera
                videoConstraints={videoConstraints}
                onCapture={handlePhotoCapture}
                onClose={handleCloseButton}
              />
            </div>
          ) : (
            <PhotoReview
              imageUrl={profileData.profilePictureUrl || ''}
              onClose={handleCloseButton}
              onRetake={handleRetake}
              onUpload={handleContinue}
            />
          )}
        </>
      );
    }

    return (
      <TabletView
        currentStep={currentStep}
        onBack={handleBack}
        onSkip={currentStep === 2 || currentStep === 3 ? handleSkip : undefined}
        isLoading={isLoading}
        showSkipButton={currentStep === 2 || currentStep === 3}
      >
        {currentStep === 1 ? (
          <div className="space-y-8">
            <div className="flex space-x-6 gap-6">
              <div className="flex-1">
                <Input
                  label={t('onboarding.firstName')}
                  value={profileData.firstName}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleChange('firstName', event.target.value)
                  }
                  placeholder={t('onboarding.firstNamePlaceholder')}
                  errorMessage={errorMessages.firstName}
                  inputClassName={`onboarding-input w-full ${
                    errorMessages.firstName
                      ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                      : ''
                  }`}
                />
              </div>

              <div className="flex-1">
                <Input
                  label={t('onboarding.lastName')}
                  value={profileData.lastName}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleChange('lastName', event.target.value)
                  }
                  placeholder={t('onboarding.lastNamePlaceholder')}
                  errorMessage={errorMessages.lastName}
                  inputClassName={`onboarding-input w-full ${
                    errorMessages.lastName
                      ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                      : ''
                  }`}
                />
              </div>
            </div>

            <div className="flex space-x-6 gap-6">
              <div className="flex-1">
                <Input
                  label={t('onboarding.userType')}
                  disabled={true}
                  value={userType}
                  placeholder={t('onboarding.userType')}
                  errorMessage={errorMessages.userType}
                  inputClassName={`onboarding-input w-full ${
                    errorMessages.userType
                      ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                      : ''
                  }`}
                />
              </div>

              <div className="flex-1">
                <Input
                  label={getOrganizationUnitLabel(userType as UserType, t)}
                  disabled={true}
                  value={organizationUnit}
                  placeholder={getOrganizationUnitLabel(
                    userType as UserType,
                    t,
                  )}
                  errorMessage={errorMessages.organizationUnit}
                  inputClassName={`onboarding-input w-full ${
                    errorMessages.organizationUnit
                      ? 'border-2 border-[#D80000] focus:ring-[#D80000]'
                      : ''
                  }`}
                />
              </div>
            </div>

            <div className="pt-8">
              <Button
                onClick={handleContinue}
                className="w-full py-4 rounded-full font-helvetica  bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <ButtonSpinner />
                ) : (
                  <Trans i18nKey="onboarding.continue" />
                )}
              </Button>
            </div>
          </div>
        ) : currentStep === 2 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 mb-8">
            <div
              onClick={handleTakePhoto}
              className="w-full aspect-[5/3] max-w-[28rem] border border-[#D8D8D8] rounded-[20px] flex flex-col items-center justify-center cursor-pointer transition-colors bg-white overflow-hidden"
            >
              {profileData.profilePictureUrl ? (
                <img
                  src={profileData.profilePictureUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="p-8 text-center">
                  <div className="mb-4">
                    <img
                      src={CameraIcon}
                      alt="Camera Icon"
                      height={100}
                      width={90}
                    />
                  </div>
                  <p className="text-[24px]">
                    <Trans i18nKey="onboarding.takePhoto" />
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center w-full">
              <FileUpload
                onFileSelect={handleFileSelect}
                onDeselect={handleFileDeselect}
                previewUrl={profileData.profilePictureUrl}
                selectedFileName={profileData.fileName}
                buttonLabel={t('onboarding.uploadFromGallery')}
                acceptedTypes="image/jpeg, image/png"
                maxSize={2}
                tabView
              />
            </div>
          </div>
        ) : currentStep === 3 ? (
          <>
            <div className="flex-1 flex flex-col items-center justify-center gap-6 mb-8">
              {!isDrawing ? (
                <>
                  {imagePreview && (
                    <div className="relative w-full aspect-[5/3] max-w-[40rem] border border-[#D8D8D8] rounded-[20px] bg-white flex items-center justify-center">
                      <img
                        src={Delete}
                        alt="Delete"
                        onClick={handleDelete}
                        className="w-5 h-5 absolute top-3 right-3 cursor-pointer z-10"
                      />
                      <img
                        src={imagePreview}
                        alt="Uploaded preview"
                        className="max-h-[90%] max-w-[90%] rounded"
                      />
                    </div>
                  )}

                  {!imagePreview && (
                    <button
                      onClick={handleSignatureDrawingClick}
                      className="relative w-full aspect-[5/3] max-w-[40rem] border border-[#D8D8D8] rounded-[20px] flex items-center justify-center bg-white"
                    >
                      <div className="p-8 text-center">
                        <p className="text-[32px] text-[#A3A3A3] cursor-pointer">
                          <Trans i18nKey="onboarding.drawSignature" />
                        </p>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleUpload}
                          onClick={(e) => e.stopPropagation()}
                          className="hidden"
                          ref={fileInputRef}
                        />

                        <div className="cursor-pointer flex flex-col items-center mt-14">
                          <img
                            src={Download}
                            alt="Upload Icon"
                            className="w-12 h-11"
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                          />
                          <div
                            className="w-40 py-2 rounded-full bg-white border text-[#1A1A1A] text-sm border-[#D8D8D8] mt-14 text-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                          >
                            <Trans i18nKey="onboarding.browseFile" />
                          </div>
                        </div>
                      </div>
                    </button>
                  )}
                </>
              ) : (
                <div className="relative w-full aspect-[5/3] max-w-[40rem] border border-[#D8D8D8] rounded-[20px] flex items-center justify-center bg-white">
                  {isSigned && (
                    <button
                      onClick={handleDelete}
                      className="absolute top-3 right-3 z-10 cursor-pointer"
                    >
                      <img
                        src={Delete}
                        alt="Delete"
                        className="w-5 h-5 text-sm text-red-500"
                      />
                    </button>
                  )}

                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    onBegin={handleSignBegin}
                    canvasProps={{
                      height: 370,
                      className:
                        'rounded-md w-full border-[#707070] rounded-[10px]',
                    }}
                  />
                </div>
              )}
            </div>
            <div className="space-y-8">
              <Button
                onClick={handleContinue}
                className="w-full py-4 rounded-full font-helvetica bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
                disabled={isLoading || (!imagePreview && !isSigned)}
              >
                {isLoading ? (
                  <ButtonSpinner />
                ) : (
                  <Trans i18nKey="onboarding.continue" />
                )}
              </Button>
            </div>
          </>
        ) : (
          <div className="pb-10 px-16">
            <div className="flex gap-20 w-50 justify-center">
              <div className="pl-[5rem]">
                <div>
                  <label className="block text-[20px] text-black font-helvetica  font-[600] w-fit">
                    <Trans i18nKey="onboarding.firstName" />
                  </label>
                  <p className="text-[20px] text-black mb-6 font-helvetica  w-fit">
                    {profileData.firstName}
                  </p>
                </div>

                <div>
                  <label className="block text-[20px] text-black  font-helvetica  font-[600] w-fit">
                    <Trans i18nKey="onboarding.lastName" />
                  </label>
                  <p className="text-[20px] text-black mb-6 font-helvetica  w-fit">
                    {profileData.lastName}
                  </p>
                </div>

                <div>
                  <label className="block text-[20px] text-black font-helvetica  font-[600] w-fit">
                    <Trans i18nKey="onboarding.userType" />
                  </label>
                  <p className="text-[20px] text-black mb-6 font-helvetica  w-fit">
                    {userType}
                  </p>
                </div>

                <div>
                  <label className="block text-[20px] text-black  font-helvetica  font-[600] w-fit">
                    {getOrganizationUnitLabel(userType as UserType, t)}
                  </label>
                  <p className="text-[20px] text-black mb-6 font-helvetica  w-fit">
                    {organizationUnit}
                  </p>
                </div>
              </div>

              <div className="flex">
                {/* Profile Picture - Larger */}
                <div className="flex-[5] pr-[2rem]">
                  <div>
                    <label className="block text-[20px] text-black mb-2 font-helvetica font-[600]">
                      <Trans i18nKey="onboarding.profilePicture" />
                    </label>
                    {profileData.profilePictureUrl ? (
                      <img
                        src={profileData.profilePictureUrl}
                        alt="Profile"
                        className="w-full aspect-[4/3] object-cover rounded-lg bg-[#F8F9FC]"
                      />
                    ) : (
                      <div className="w-full aspect-[4/3] rounded-lg bg-[#F8F9FC] flex items-center justify-center">
                        <p className="text-gray-500">
                          <Trans i18nKey="onboarding.noProfilePicture" />
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-[2] pr-[5rem]">
                  <div>
                    <label className="block text-[20px] text-black mb-2 font-helvetica font-[600]">
                      <Trans i18nKey="onboarding.signature" />
                    </label>
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Signature"
                        className="w-full aspect-[4/3] object-contain rounded-lg bg-[#F8F9FC]"
                      />
                    ) : (
                      <div className="w-full aspect-[4/3] rounded-lg bg-[#F8F9FC] flex items-center justify-center">
                        <p className="text-gray-500">
                          <Trans i18nKey="onboarding.noSignature" />
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16 flex justify-center">
              <Button
                onClick={handleContinue}
                className="w-full py-4 rounded-full font-helvetica  bg-THEME_COLOR hover:bg-THEME_HOVER_COLOR text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <ButtonSpinner />
                ) : (
                  <Trans i18nKey="onboarding.finish" />
                )}
              </Button>
            </div>
          </div>
        )}
      </TabletView>
    );
  }
};
